import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useFamily } from "@/portals/patient/context/FamilyContext";

/**
 * `?profile=<id>` (from a review notification) selects whose records to show,
 * through the existing FamilyContext switch: an own dependent's id switches to
 * that dependent; any other id is the patient's own profile. Waits for the
 * dependents list so a dependent isn't mistaken for "self" while it's still
 * loading, then drops the param so a refresh doesn't re-apply it.
 *
 * Returns true while a requested switch hasn't been applied yet — callers that
 * fetch profile-scoped data should wait, instead of fetching for the wrong profile.
 */
export function useProfileFromQuery(): boolean {
  const [searchParams, setSearchParams] = useSearchParams();
  const { dependents, isLoading, setActiveDependentId } = useFamily();
  const requested = searchParams.get("profile");

  useEffect(() => {
    if (!requested || isLoading) return;
    setActiveDependentId(dependents.some((d) => d.id === requested) ? requested : null);
    setSearchParams((params) => {
      params.delete("profile");
      return params;
    }, { replace: true });
  }, [requested, isLoading, dependents, setActiveDependentId, setSearchParams]);

  return Boolean(requested);
}
